import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  User,
  Link2,
  CheckCircle,
  X,
  AlertCircle,
  Mail,
  Calendar,
  Shield,
  Globe,
  Store,
  FileText,
  Settings,
  ExternalLink
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { HelpCircle, BookOpen } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { GoogleIcon } from "@/components/ui/GoogleIcon";
import { toast } from "sonner";
import type { User as SupabaseUser } from "@supabase/supabase-js";
import { PLATFORM_IMAGES } from "@/constants/platformImages";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { SubscriptionManagement } from "./SubscriptionManagement";
import { Separator } from "@/components/ui/separator";

interface UserSettingsData {
  id?: string;
  google_search_console_access_token: string | null;
  google_search_console_refresh_token: string | null;
  google_search_console_connected_at: string | null;
  platform_connections?: {
    wordpress?: {
      connected: boolean;
      siteUrl?: string;
      username?: string;
      appPassword?: string;
    };
    shopify?: {
      connected: boolean;
      storeUrl?: string;
      accessToken?: string;
    };
    wix?: {
      connected: boolean;
      siteId?: string;
      accessToken?: string;
    };
    webflow?: {
      connected: boolean;
      apiKey?: string;
      siteId?: string;
      collectionId?: string;
    };
    framer?: {
      connected: boolean;
      apiKey?: string;
      siteId?: string;
      collectionId?: string;
    };
  };
}

interface SimpleSettingsTabProps {
  user: SupabaseUser;
  isActive?: boolean;
}

export const SimpleSettingsTab: React.FC<SimpleSettingsTabProps> = ({ user, isActive = true }) => {
  const [settings, setSettings] = useState<UserSettingsData | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const navigate = useNavigate();
  
  // Platform connection states
  const [platformDialogOpen, setPlatformDialogOpen] = useState<string | null>(null);
  const [platformFormData, setPlatformFormData] = useState<{
    wordpress?: { siteUrl: string; username?: string; appPassword?: string; useManual?: boolean };
    shopify?: { adminApiKey: string; domain: string; selectedBlogId?: string };
    wix?: { siteId: string; accessToken: string };
    webflow?: { apiKey: string; siteId?: string; collectionId?: string };
    framer?: { apiKey: string; siteId?: string; collectionId?: string };
  }>({});
  const [isSavingPlatform, setIsSavingPlatform] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [shopifyBlogs, setShopifyBlogs] = useState<any[]>([]);
  const [showBlogSelection, setShowBlogSelection] = useState(false);
  const [webflowCollections, setWebflowCollections] = useState<any[]>([]);
  const [showCollectionSelection, setShowCollectionSelection] = useState(false);
  const [webflowSites, setWebflowSites] = useState<any[]>([]);
  const [showSiteSelection, setShowSiteSelection] = useState(false);
  const [framerCollections, setFramerCollections] = useState<any[]>([]);
  const [showFramerCollectionSelection, setShowFramerCollectionSelection] = useState(false);
  const [framerSites, setFramerSites] = useState<any[]>([]);
  const [showFramerSiteSelection, setShowFramerSiteSelection] = useState(false);
  
  // Track if data has been loaded initially
  const [loadedUserId, setLoadedUserId] = useState<string | null>(null);

  useEffect(() => {
    // Only load data if tab is active
    if (!isActive) return;
    
    // Reset loaded state if user changed
    if (user?.id !== loadedUserId) {
      setLoadedUserId(null);
    }
    
    if (user?.id && user.id !== loadedUserId) {
      loadSettings(user.id);
      setLoadedUserId(user.id);
    }
  }, [user?.id, isActive, loadedUserId]);

  const loadSettings = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_settings')
        .select('id, google_search_console_access_token, google_search_console_refresh_token, google_search_console_connected_at, platform_connections')
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      setSettings(data);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const connectGoogleSearchConsole = async () => {
    setIsConnecting(true);
    try {
      const { data: clientConfig, error: configError } = await supabase.functions.invoke('get-oauth-config', {});
      
      if (configError || !clientConfig?.clientId) {
        throw new Error('OAuth configuration not available');
      }
      const redirectUri = `${window.location.origin}/oauth/google-search-console`;
      const scope = [
        "https://www.googleapis.com/auth/webmasters.readonly",
        "https://www.googleapis.com/auth/userinfo.email",
        "https://www.googleapis.com/auth/userinfo.profile"
      ].join(" ");
      
      const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${clientConfig.clientId}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `scope=${encodeURIComponent(scope)}&` +
        `response_type=code&` +
        `access_type=offline&` +
        `prompt=consent`;

      const popup = window.open(authUrl, 'google-oauth', 'width=500,height=600');
      
      const pollTimer = setInterval(() => {
        try {
          if (popup?.closed) {
            clearInterval(pollTimer);
            setIsConnecting(false);
          }
        } catch (error) {
          clearInterval(pollTimer);
          setIsConnecting(false);
        }
      }, 1000);

      const messageListener = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        
        if (event.data.type === 'GOOGLE_OAUTH_SUCCESS') {
          clearInterval(pollTimer);
          popup?.close();
          window.removeEventListener('message', messageListener);
          saveGoogleTokens(event.data.tokens);
          setIsConnecting(false);
        } else if (event.data.type === 'GOOGLE_OAUTH_ERROR') {
          clearInterval(pollTimer);
          popup?.close();
          window.removeEventListener('message', messageListener);
          toast.error('Failed to connect Google Search Console');
          setIsConnecting(false);
        }
      };
      
      window.addEventListener('message', messageListener);
      
    } catch (error) {
      console.error('Error connecting to Google Search Console:', error);
      toast.error('Failed to connect to Google Search Console');
      setIsConnecting(false);
    }
  };

  const saveGoogleTokens = async (tokens: any) => {
    if (!user?.id) return;

    try {
      // Tokens are already stored in the backend during OAuth exchange
      // Just reload settings to get the updated connection status
      await loadSettings(user.id);
      
      // Small delay to ensure database is updated
      setTimeout(async () => {
        await loadSettings(user.id);
      }, 500);
      
      toast.success(`Google Search Console connected successfully!${tokens.site_url ? ` Site: ${tokens.site_url}` : ''}`);
    } catch (error) {
      console.error('Error handling Google tokens:', error);
      toast.error('Failed to complete Google Search Console connection');
    }
  };

  const disconnectGoogleSearchConsole = async () => {
    if (!user?.id) return;

    setIsDisconnecting(true);
    try {
      const { error } = await supabase
        .from('user_settings')
        .update({
          google_search_console_access_token: null,
          google_search_console_refresh_token: null,
          google_search_console_connected_at: null,
        })
        .eq('user_id', user.id);

      if (error) throw error;

      toast.success('Google Search Console disconnected');
      loadSettings(user.id);
    } catch (error) {
      console.error('Error disconnecting Google Search Console:', error);
      toast.error('Failed to disconnect Google Search Console');
    } finally {
      setIsDisconnecting(false);
    }
  };

  const isConnected = settings?.google_search_console_access_token;
  const platformConnections = settings?.platform_connections || {};

  const handlePlatformConnect = (platform: string) => {
    setPlatformDialogOpen(platform);
    setShowBlogSelection(false);
    setShopifyBlogs([]);
    // Pre-fill form if already connected
    const existing = platformConnections[platform as keyof typeof platformConnections];
    if (existing?.connected) {
      if (platform === 'wordpress') {
        setPlatformFormData({
          wordpress: {
            siteUrl: existing.siteUrl || '',
            username: existing.username || '',
            appPassword: '',
            useManual: true
          }
        });
      } else if (platform === 'shopify') {
        setPlatformFormData({
          shopify: {
            adminApiKey: '',
            domain: existing.domain || '',
            selectedBlogId: existing.selectedBlogId
          }
        });
      } else if (platform === 'wix') {
        setPlatformFormData({
          wix: {
            siteId: existing.siteId || '',
            accessToken: ''
          }
        });
      } else if (platform === 'webflow') {
        setPlatformFormData({
          webflow: {
            apiKey: existing.apiKey || '',
            siteId: existing.siteId || '',
            collectionId: existing.collectionId || ''
          }
        });
      } else if (platform === 'framer') {
        setPlatformFormData({
          framer: {
            apiKey: existing.apiKey || '',
            siteId: existing.siteId || '',
            collectionId: existing.collectionId || ''
          }
        });
      }
    } else {
      // Initialize empty form
      if (platform === 'wordpress') {
        setPlatformFormData({ wordpress: { siteUrl: '', useManual: true } });
      } else if (platform === 'shopify') {
        setPlatformFormData({ shopify: { adminApiKey: '', domain: '' } });
      } else if (platform === 'wix') {
        setPlatformFormData({ wix: { siteId: '', accessToken: '' } });
      } else if (platform === 'webflow') {
        setPlatformFormData({ webflow: { apiKey: '', siteId: '', collectionId: '' } });
        setShowCollectionSelection(false);
        setShowSiteSelection(false);
        setWebflowCollections([]);
        setWebflowSites([]);
      } else if (platform === 'framer') {
        setPlatformFormData({ framer: { apiKey: '', siteId: '', collectionId: '' } });
        setShowFramerCollectionSelection(false);
        setShowFramerSiteSelection(false);
        setFramerCollections([]);
        setFramerSites([]);
      }
    }
  };

  const verifyShopify = async () => {
    const { adminApiKey, domain } = platformFormData.shopify || {};
    if (!adminApiKey || !domain) {
      toast.error('Please enter both Admin API key and domain');
      return;
    }

    setIsVerifying(true);
    try {
      const { data, error } = await supabase.functions.invoke('verify-shopify', {
        body: { adminApiKey, domain }
      });

      if (error || !data?.success) {
        toast.error(data?.error || 'Failed to verify Shopify credentials');
        return;
      }

      if (data.blogs && data.blogs.length > 0) {
        setShopifyBlogs(data.blogs);
        setShowBlogSelection(true);
        toast.success('Credentials verified! Please select a blog.');
      } else {
        toast.error('No blogs found in your Shopify store');
      }
    } catch (error: any) {
      console.error('Error verifying Shopify:', error);
      toast.error(error.message || 'Failed to verify Shopify credentials');
    } finally {
      setIsVerifying(false);
    }
  };

  const verifyWordPress = async () => {
    const { siteUrl, username, appPassword } = platformFormData.wordpress || {};
    if (!siteUrl) {
      toast.error('Please enter your WordPress site URL');
      return;
    }

    // Ensure URL has https:// protocol
    let cleanUrl = siteUrl.trim();
    if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
      cleanUrl = `https://${cleanUrl}`;
    }
    // Prefer https over http
    cleanUrl = cleanUrl.replace(/^http:\/\//, 'https://');
    // Remove trailing slash
    cleanUrl = cleanUrl.replace(/\/$/, '');

    if (!username || !appPassword) {
      toast.error('Please enter both username and application password');
      return;
    }

    setIsVerifying(true);
    try {
      const { data, error } = await supabase.functions.invoke('verify-wordpress', {
        body: { siteUrl: cleanUrl, username, appPassword }
      });

      if (error || !data?.success) {
        toast.error(data?.error || 'Failed to verify WordPress credentials');
        return;
      }

      // Update form data with cleaned URL
      setPlatformFormData({
        ...platformFormData,
        wordpress: { ...platformFormData.wordpress, siteUrl: cleanUrl, useManual: true } as any
      });

      // Save connection
      await savePlatformConnection('wordpress');
    } catch (error: any) {
      console.error('Error verifying WordPress:', error);
      toast.error(error.message || 'Failed to verify WordPress credentials');
    } finally {
      setIsVerifying(false);
    }
  };

  const verifyWebflow = async () => {
    const { apiKey, siteId, collectionId } = platformFormData.webflow || {};
    if (!apiKey) {
      toast.error('Please enter your Webflow API key');
      return;
    }

    setIsVerifying(true);
    try {
      // First verification: just API key to get sites
      const { data, error } = await supabase.functions.invoke('verify-webflow', {
        body: { apiKey, siteId, collectionId }
      });

      if (error || !data?.success) {
        toast.error(data?.error || 'Failed to verify Webflow credentials');
        return;
      }

      // If no siteId, show site selection
      if (!siteId && data.sites && data.sites.length > 0) {
        if (data.sites.length === 1) {
          // Auto-select if only one site
          setPlatformFormData({
            ...platformFormData,
            webflow: { ...platformFormData.webflow, siteId: data.sites[0].id } as any
          });
          // Continue to fetch collections for this site
          await fetchWebflowCollections(apiKey, data.sites[0].id);
        } else {
          // Show site selection
          setWebflowSites(data.sites);
          setShowSiteSelection(true);
          toast.success('Credentials verified! Please select a site.');
        }
        return;
      }

      // If siteId is set but no collectionId, fetch collections
      if (siteId && !collectionId) {
        await fetchWebflowCollections(apiKey, siteId);
        return;
      }

      // If both siteId and collectionId are set, save connection
      if (siteId && collectionId) {
        toast.success('Webflow credentials verified!');
        await savePlatformConnection('webflow');
      }
    } catch (error: any) {
      console.error('Error verifying Webflow:', error);
      toast.error(error.message || 'Failed to verify Webflow credentials');
    } finally {
      setIsVerifying(false);
    }
  };

  const fetchWebflowCollections = async (apiKey: string, siteId: string) => {
    setIsVerifying(true);
    try {
      const { data, error } = await supabase.functions.invoke('verify-webflow', {
        body: { apiKey, siteId }
      });

      if (error || !data?.success) {
        toast.error(data?.error || 'Failed to fetch collections');
        return;
      }

      if (data.collections && data.collections.length > 0) {
        // Find "blog posts" or similar collection and mark as recommended
        const collections = data.collections.map((collection: any) => {
          const nameLower = (collection.name || '').toLowerCase();
          const isBlogPosts = nameLower.includes('blog') || 
                             nameLower.includes('post') || 
                             nameLower === 'blog posts' ||
                             nameLower === 'posts';
          return {
            ...collection,
            recommended: isBlogPosts
          };
        });
        
        // Sort: recommended first
        collections.sort((a: any, b: any) => {
          if (a.recommended && !b.recommended) return -1;
          if (!a.recommended && b.recommended) return 1;
          return 0;
        });
        
        setWebflowCollections(collections);
        setShowCollectionSelection(true);
        setShowSiteSelection(false);
        
        // Auto-select recommended collection if found
        const recommended = collections.find((c: any) => c.recommended);
        if (recommended) {
          setPlatformFormData({
            ...platformFormData,
            webflow: { ...platformFormData.webflow, collectionId: recommended.id } as any
          });
        }
        
        toast.success('Please select a collection.');
      } else {
        toast.error('No collections found. Please create a CMS collection in Webflow first.');
      }
    } catch (error: any) {
      console.error('Error fetching collections:', error);
      toast.error(error.message || 'Failed to fetch collections');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleWebflowSiteSelect = async (selectedSiteId: string) => {
    const { apiKey } = platformFormData.webflow || {};
    setPlatformFormData({
      ...platformFormData,
      webflow: { ...platformFormData.webflow, siteId: selectedSiteId } as any
    });
    await fetchWebflowCollections(apiKey || '', selectedSiteId);
  };

  const verifyWix = async () => {
    const { siteId, accessToken } = platformFormData.wix || {};
    if (!siteId || !accessToken) {
      toast.error('Please enter both Site ID and Access Token');
      return;
    }

    setIsVerifying(true);
    try {
      const { data, error } = await supabase.functions.invoke('verify-wix', {
        body: { siteId, accessToken }
      });

      if (error || !data?.success) {
        toast.error(data?.error || 'Failed to verify Wix credentials');
        return;
      }

      toast.success('Wix credentials verified!');
      // Save connection
      await savePlatformConnection('wix');
    } catch (error: any) {
      console.error('Error verifying Wix:', error);
      toast.error(error.message || 'Failed to verify Wix credentials');
    } finally {
      setIsVerifying(false);
    }
  };

  const verifyFramer = async () => {
    const { apiKey, siteId, collectionId } = platformFormData.framer || {};
    if (!apiKey) {
      toast.error('Please enter your Framer API key');
      return;
    }

    setIsVerifying(true);
    try {
      // First verification: just API key to get sites
      const { data, error } = await supabase.functions.invoke('verify-framer', {
        body: { apiKey, siteId, collectionId }
      });

      if (error || !data?.success) {
        toast.error(data?.error || 'Failed to verify Framer credentials');
        return;
      }

      // If no siteId, show site selection
      if (!siteId && data.sites && data.sites.length > 0) {
        if (data.sites.length === 1) {
          // Auto-select if only one site
          setPlatformFormData({
            ...platformFormData,
            framer: { ...platformFormData.framer, siteId: data.sites[0].id } as any
          });
          // Continue to fetch collections for this site
          await fetchFramerCollections(apiKey, data.sites[0].id);
        } else {
          // Show site selection
          setFramerSites(data.sites);
          setShowFramerSiteSelection(true);
          toast.success('Credentials verified! Please select a site.');
        }
        return;
      }

      // If siteId is set but no collectionId, fetch collections
      if (siteId && !collectionId) {
        await fetchFramerCollections(apiKey, siteId);
        return;
      }

      // If both siteId and collectionId are set, save connection
      if (siteId && collectionId) {
        toast.success('Framer credentials verified!');
        await savePlatformConnection('framer');
      }
    } catch (error: any) {
      console.error('Error verifying Framer:', error);
      toast.error(error.message || 'Failed to verify Framer credentials');
    } finally {
      setIsVerifying(false);
    }
  };

  const fetchFramerCollections = async (apiKey: string, siteId: string) => {
    setIsVerifying(true);
    try {
      const { data, error } = await supabase.functions.invoke('verify-framer', {
        body: { apiKey, siteId }
      });

      if (error || !data?.success) {
        toast.error(data?.error || 'Failed to fetch collections');
        return;
      }

      if (data.collections && data.collections.length > 0) {
        // Find "blog posts" or similar collection and mark as recommended
        const collections = data.collections.map((collection: any) => {
          const nameLower = (collection.name || '').toLowerCase();
          const isBlogPosts = nameLower.includes('blog') || 
                             nameLower.includes('post') || 
                             nameLower === 'blog posts' ||
                             nameLower === 'posts';
          return {
            ...collection,
            recommended: isBlogPosts
          };
        });
        
        // Sort: recommended first
        collections.sort((a: any, b: any) => {
          if (a.recommended && !b.recommended) return -1;
          if (!a.recommended && b.recommended) return 1;
          return 0;
        });
        
        setFramerCollections(collections);
        setShowFramerCollectionSelection(true);
        setShowFramerSiteSelection(false);
        
        // Auto-select recommended collection if found
        const recommended = collections.find((c: any) => c.recommended);
        if (recommended) {
          setPlatformFormData({
            ...platformFormData,
            framer: { ...platformFormData.framer, collectionId: recommended.id } as any
          });
        }
        
        toast.success('Please select a collection.');
      } else {
        toast.error('No collections found. Please create a CMS collection in Framer first.');
      }
    } catch (error: any) {
      console.error('Error fetching collections:', error);
      toast.error(error.message || 'Failed to fetch collections');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleFramerSiteSelect = async (selectedSiteId: string) => {
    const { apiKey } = platformFormData.framer || {};
    setPlatformFormData({
      ...platformFormData,
      framer: { ...platformFormData.framer, siteId: selectedSiteId } as any
    });
    await fetchFramerCollections(apiKey || '', selectedSiteId);
  };

  const savePlatformConnection = async (platform: string) => {
    if (!user?.id) return;

    setIsSavingPlatform(true);
    try {
      const currentConnections = platformConnections || {};
      let updatedConnection: any = {};

      if (platform === 'wordpress') {
        const { siteUrl, username, appPassword } = platformFormData.wordpress || {};
        if (!siteUrl || !username || !appPassword) {
          toast.error('Please fill in all WordPress fields');
          setIsSavingPlatform(false);
          return;
        }
        updatedConnection = {
          ...currentConnections,
          wordpress: {
            connected: true,
            siteUrl: siteUrl.replace(/\/$/, ''),
            username,
            appPassword
          }
        };
      } else if (platform === 'shopify') {
        const { adminApiKey, domain, selectedBlogId } = platformFormData.shopify || {};
        if (!adminApiKey || !domain || !selectedBlogId) {
          toast.error('Please verify credentials and select a blog');
          setIsSavingPlatform(false);
          return;
        }
        updatedConnection = {
          ...currentConnections,
          shopify: {
            connected: true,
            adminApiKey,
            domain: domain.replace(/^https?:\/\//, '').replace(/\/$/, ''),
            selectedBlogId
          }
        };
      } else if (platform === 'wix') {
        const { siteId, accessToken } = platformFormData.wix || {};
        if (!siteId || !accessToken) {
          toast.error('Please fill in all Wix fields');
          setIsSavingPlatform(false);
          return;
        }
        updatedConnection = {
          ...currentConnections,
          wix: {
            connected: true,
            siteId,
            accessToken
          }
        };
      } else if (platform === 'webflow') {
        const { apiKey, siteId, collectionId } = platformFormData.webflow || {};
        if (!apiKey || !siteId || !collectionId) {
          toast.error('Please enter API key, Site ID, and Collection ID');
          setIsSavingPlatform(false);
          return;
        }
        updatedConnection = {
          ...currentConnections,
          webflow: {
            connected: true,
            apiKey,
            siteId,
            collectionId
          }
        };
      } else if (platform === 'framer') {
        const { apiKey, siteId, collectionId } = platformFormData.framer || {};
        if (!apiKey || !siteId || !collectionId) {
          toast.error('Please enter API key, Site ID, and Collection ID');
          setIsSavingPlatform(false);
          return;
        }
        updatedConnection = {
          ...currentConnections,
          framer: {
            connected: true,
            apiKey,
            siteId,
            collectionId
          }
        };
      }

      const { error } = await supabase
        .from('user_settings')
        .upsert({
          user_id: user.id,
          platform_connections: updatedConnection
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;

      toast.success(`${platform.charAt(0).toUpperCase() + platform.slice(1)} connected successfully!`);
      setPlatformDialogOpen(null);
      setPlatformFormData({});
      setShowBlogSelection(false);
      setShopifyBlogs([]);
      setShowCollectionSelection(false);
      setShowSiteSelection(false);
      setWebflowCollections([]);
      setWebflowSites([]);
      setShowFramerCollectionSelection(false);
      setShowFramerSiteSelection(false);
      setFramerCollections([]);
      setFramerSites([]);
      loadSettings(user.id);
    } catch (error) {
      console.error(`Error saving ${platform} connection:`, error);
      toast.error(`Failed to connect ${platform}`);
    } finally {
      setIsSavingPlatform(false);
    }
  };

  const disconnectPlatform = async (platform: string) => {
    if (!user?.id) return;

    try {
      const currentConnections = platformConnections || {};
      const updatedConnection = {
        ...currentConnections,
        [platform]: {
          connected: false
        }
      };

      const { error } = await supabase
        .from('user_settings')
        .upsert({
          user_id: user.id,
          platform_connections: updatedConnection
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;

      toast.success(`${platform.charAt(0).toUpperCase() + platform.slice(1)} disconnected`);
      loadSettings(user.id);
    } catch (error) {
      console.error(`Error disconnecting ${platform}:`, error);
      toast.error(`Failed to disconnect ${platform}`);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold text-foreground tracking-tight">Settings</h2>
        <p className="text-muted-foreground text-lg font-medium mt-2">Manage your account and integrations</p>
      </div>

      {/* Account Overview */}
      <Card className="
        bg-background/95
        backdrop-blur-lg
        border border-border/30
        shadow-[0_3px_16px_rgba(0,0,0,0.04),0_1px_6px_rgba(0,0,0,0.02)]
        hover:shadow-[0_6px_24px_rgba(0,0,0,0.06),0_2px_10px_rgba(0,0,0,0.03)]
        rounded-3xl
        transition-all duration-500 ease-out
      ">
        <CardHeader className="pb-6 pt-8 px-8">
          <CardTitle className="text-2xl font-bold text-foreground tracking-tight flex items-center gap-3">
            <div className="text-primary/80 p-3 rounded-2xl bg-primary/10 border border-primary/20 shadow-[0_1px_3px_rgba(0,0,0,0.1)]">
              <User className="h-5 w-5" />
            </div>
            Account Overview
          </CardTitle>
        </CardHeader>
        <CardContent className="px-8 pb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center gap-4 p-6 rounded-3xl bg-gradient-to-br from-muted/30 to-muted/10 backdrop-blur-md border border-border/20 shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.04)] transition-all duration-300">
              <div className="text-primary/80 p-3 rounded-2xl bg-primary/10 border border-primary/20 shadow-[0_1px_3px_rgba(0,0,0,0.1)]">
                <Mail className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-muted-foreground tracking-wide mb-1">Email</p>
                <p className="text-base font-bold text-foreground">{user.email}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 p-6 rounded-3xl bg-gradient-to-br from-muted/30 to-muted/10 backdrop-blur-md border border-border/20 shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.04)] transition-all duration-300">
              <div className="text-primary/80 p-3 rounded-2xl bg-primary/10 border border-primary/20 shadow-[0_1px_3px_rgba(0,0,0,0.1)]">
                <Calendar className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-muted-foreground tracking-wide mb-1">Member Since</p>
                <p className="text-base font-bold text-foreground">{new Date(user.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Integrations */}
      <Card className="
        bg-background/95
        backdrop-blur-lg
        border border-border/30
        shadow-[0_3px_16px_rgba(0,0,0,0.04),0_1px_6px_rgba(0,0,0,0.02)]
        hover:shadow-[0_6px_24px_rgba(0,0,0,0.06),0_2px_10px_rgba(0,0,0,0.03)]
        rounded-3xl
        transition-all duration-500 ease-out
      ">
        <CardHeader className="pb-6 pt-8 px-8">
          <CardTitle className="text-2xl font-bold text-foreground tracking-tight flex items-center gap-3">
            <div className="text-primary/80 p-3 rounded-2xl bg-primary/10 border border-primary/20 shadow-[0_1px_3px_rgba(0,0,0,0.1)]">
              <Link2 className="h-5 w-5" />
            </div>
            Integrations
          </CardTitle>
        </CardHeader>
        <CardContent className="px-8 pb-8">
          <div className="p-6 rounded-3xl bg-gradient-to-br from-muted/30 to-muted/10 backdrop-blur-md border border-border/20 shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.04)] transition-all duration-300">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 rounded-2xl bg-background/90 border border-border/30 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                    <GoogleIcon size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-foreground leading-tight">Google Search Console</h3>
                    <p className="text-sm text-muted-foreground font-medium mt-1">Connect for real analytics data</p>
                  </div>
                </div>
                
                {isConnected ? (
                  <div className="flex items-center gap-3 flex-wrap">
                    <Badge variant="default" className="rounded-full px-3 py-1.5 font-semibold text-sm bg-green-500/10 text-green-600 border-green-500/20">
                      <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                      Connected
                    </Badge>
                    <span className="text-sm text-muted-foreground font-medium">
                      Connected on {new Date(settings?.google_search_console_connected_at || '').toLocaleDateString()}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 flex-wrap">
                    <Badge variant="outline" className="rounded-full px-3 py-1.5 font-semibold text-sm text-muted-foreground border-border/50">
                      Not Connected
                    </Badge>
                    <span className="text-sm text-muted-foreground font-medium">
                      Connect to view real analytics data
                    </span>
                  </div>
                )}
              </div>
              
              <div className="flex-shrink-0">
                {isConnected ? (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button 
                        variant="outline" 
                        disabled={isDisconnecting} 
                        className="rounded-full px-6 py-3 text-base font-semibold border-2 border-border/50 bg-background/90 backdrop-blur-sm text-foreground hover:bg-destructive hover:text-destructive-foreground hover:border-destructive transition-all duration-500 ease-in-out shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <X className="h-4 w-4 mr-2" />
                        {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-3xl">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-xl font-bold">Disconnect Google Search Console?</AlertDialogTitle>
                        <AlertDialogDescription className="text-base leading-relaxed">
                          This will remove access to your Google Search Console data. 
                          You can reconnect at any time.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter className="gap-2">
                        <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={disconnectGoogleSearchConsole}
                          className="rounded-full bg-destructive hover:bg-destructive/90"
                        >
                          Disconnect
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                ) : (
                  <Button 
                    onClick={connectGoogleSearchConsole} 
                    disabled={isConnecting}
                    className="rounded-full px-6 py-3 text-base font-semibold bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground hover:from-primary/95 hover:via-primary/90 hover:to-primary/85 transition-all duration-500 ease-in-out shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Link2 className="h-4 w-4 mr-2" />
                    {isConnecting ? 'Connecting...' : 'Connect'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Publishing Platforms */}
      <Card className="
        bg-background/95
        backdrop-blur-lg
        border border-border/30
        shadow-[0_3px_16px_rgba(0,0,0,0.04),0_1px_6px_rgba(0,0,0,0.02)]
        hover:shadow-[0_6px_24px_rgba(0,0,0,0.06),0_2px_10px_rgba(0,0,0,0.03)]
        rounded-3xl
        transition-all duration-500 ease-out
      ">
        <CardHeader className="pb-6 pt-8 px-8">
          <CardTitle className="text-2xl font-bold text-foreground tracking-tight flex items-center gap-3">
            <div className="text-primary/80 p-3 rounded-2xl bg-primary/10 border border-primary/20 shadow-[0_1px_3px_rgba(0,0,0,0.1)]">
              <ExternalLink className="h-5 w-5" />
            </div>
            Publishing Platforms
          </CardTitle>
          <p className="text-muted-foreground text-sm font-medium mt-2">
            Connect your website platforms to publish articles directly from SEORO
          </p>
        </CardHeader>
        <CardContent className="px-8 pb-8">
          <div className="space-y-4">
            {/* WordPress */}
            <div className="p-6 rounded-3xl bg-gradient-to-br from-muted/30 to-muted/10 backdrop-blur-md border border-border/20 shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.04)] transition-all duration-300">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 rounded-2xl bg-background/90 border border-border/30 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                      <img src={PLATFORM_IMAGES.wordpress} alt="WordPress" className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-foreground leading-tight">WordPress</h3>
                      <p className="text-sm text-muted-foreground font-medium mt-1">Publish articles to your WordPress site</p>
                    </div>
                  </div>
                  
                  {platformConnections.wordpress?.connected ? (
                    <div className="flex items-center gap-3 flex-wrap">
                      <Badge variant="default" className="rounded-full px-3 py-1.5 font-semibold text-sm bg-green-500/10 text-green-600 border-green-500/20">
                        <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                        Connected
                      </Badge>
                      <span className="text-sm text-muted-foreground font-medium">
                        {platformConnections.wordpress.siteUrl}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 flex-wrap">
                      <Badge variant="outline" className="rounded-full px-3 py-1.5 font-semibold text-sm text-muted-foreground border-border/50">
                        Not Connected
                      </Badge>
                    </div>
                  )}
                </div>
                
                <div className="flex-shrink-0">
                  {platformConnections.wordpress?.connected ? (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button 
                          variant="outline" 
                          className="rounded-full px-6 py-3 text-base font-semibold border-2 border-border/50 bg-background/90 backdrop-blur-sm text-foreground hover:bg-destructive hover:text-destructive-foreground hover:border-destructive transition-all duration-500 ease-in-out shadow-sm"
                        >
                          <X className="h-4 w-4 mr-2" />
                          Disconnect
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="rounded-3xl">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-xl font-bold">Disconnect WordPress?</AlertDialogTitle>
                          <AlertDialogDescription className="text-base leading-relaxed">
                            This will remove your WordPress connection. You can reconnect at any time.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter className="gap-2">
                          <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => disconnectPlatform('wordpress')}
                            className="rounded-full bg-destructive hover:bg-destructive/90"
                          >
                            Disconnect
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  ) : (
                    <Dialog open={platformDialogOpen === 'wordpress'} onOpenChange={(open) => setPlatformDialogOpen(open ? 'wordpress' : null)}>
                      <DialogTrigger asChild>
                        <Button 
                          onClick={() => handlePlatformConnect('wordpress')}
                          className="rounded-full px-6 py-3 text-base font-semibold bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground hover:from-primary/95 hover:via-primary/90 hover:to-primary/85 transition-all duration-500 ease-in-out shadow-lg hover:shadow-xl"
                        >
                          <Link2 className="h-4 w-4 mr-2" />
                          Connect
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="rounded-3xl max-w-md">
                        <DialogHeader>
                          <div className="flex items-center justify-between">
                            <div>
                              <DialogTitle className="text-xl font-bold">Connect WordPress</DialogTitle>
                              <DialogDescription className="text-sm text-muted-foreground mt-1">
                                Connect your WordPress site to publish articles directly
                              </DialogDescription>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.location.href = '/docs/wordpress-integration'}
                              className="text-muted-foreground hover:text-foreground"
                            >
                              <BookOpen className="h-4 w-4 mr-1" />
                              Docs
                            </Button>
                          </div>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="space-y-2">
                            <Label htmlFor="wp-site-url-manual">WordPress Site URL</Label>
                            <div className="flex items-center gap-0 border border-input rounded-md bg-background">
                              <span className="px-3 py-2 text-sm text-muted-foreground border-r border-input bg-muted/50">
                                https://
                              </span>
                              <Input
                                id="wp-site-url-manual"
                                placeholder="yoursite.com"
                                value={platformFormData.wordpress?.siteUrl?.replace(/^https?:\/\//, '') || ''}
                                onChange={(e) => {
                                  let value = e.target.value.trim();
                                  // Remove protocol if user types it
                                  value = value.replace(/^https?:\/\//, '');
                                  // Always ensure it starts with https:// when saving
                                  const urlWithProtocol = value ? `https://${value}` : '';
                                  setPlatformFormData({
                                    ...platformFormData,
                                    wordpress: { ...platformFormData.wordpress, siteUrl: urlWithProtocol, useManual: true } as any
                                  });
                                }}
                                className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 rounded-l-none"
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="wp-username">WordPress Username</Label>
                            <Input
                              id="wp-username"
                              placeholder="your-username"
                              value={platformFormData.wordpress?.username || ''}
                              onChange={(e) => setPlatformFormData({
                                ...platformFormData,
                                wordpress: { ...platformFormData.wordpress, username: e.target.value, useManual: true } as any
                              })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="wp-password">WordPress Application Password</Label>
                            <Input
                              id="wp-password"
                              type="password"
                              placeholder="xxxx xxxx xxxx xxxx"
                              value={platformFormData.wordpress?.appPassword || ''}
                              onChange={(e) => setPlatformFormData({
                                ...platformFormData,
                                wordpress: { ...platformFormData.wordpress, appPassword: e.target.value, useManual: true } as any
                              })}
                            />
                            <p className="text-xs text-muted-foreground flex items-start gap-1">
                              <HelpCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                              Create an Application Password: Users → Profile → Application Passwords
                            </p>
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => {
                            setPlatformDialogOpen(null);
                            setPlatformFormData({});
                          }} className="rounded-full">
                            Cancel
                          </Button>
                          <Button 
                            onClick={verifyWordPress}
                            disabled={isVerifying || isSavingPlatform}
                            className="rounded-full bg-gradient-to-r from-primary via-primary to-primary/90"
                          >
                            {isVerifying ? 'Verifying...' : isSavingPlatform ? 'Connecting...' : 'Verify & Connect'}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </div>
            </div>

            {/* Shopify */}
            <div className="p-6 rounded-3xl bg-gradient-to-br from-muted/30 to-muted/10 backdrop-blur-md border border-border/20 shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.04)] transition-all duration-300">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 rounded-2xl bg-background/90 border border-border/30 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                      <img src={PLATFORM_IMAGES.shopify} alt="Shopify" className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-foreground leading-tight">Shopify</h3>
                      <p className="text-sm text-muted-foreground font-medium mt-1">Publish articles to your Shopify blog</p>
                    </div>
                  </div>
                  
                  {platformConnections.shopify?.connected ? (
                    <div className="flex items-center gap-3 flex-wrap">
                      <Badge variant="default" className="rounded-full px-3 py-1.5 font-semibold text-sm bg-green-500/10 text-green-600 border-green-500/20">
                        <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                        Connected
                      </Badge>
                      <span className="text-sm text-muted-foreground font-medium">
                        {platformConnections.shopify.domain || platformConnections.shopify.storeUrl}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 flex-wrap">
                      <Badge variant="outline" className="rounded-full px-3 py-1.5 font-semibold text-sm text-muted-foreground border-border/50">
                        Not Connected
                      </Badge>
                    </div>
                  )}
                </div>
                
                <div className="flex-shrink-0">
                  {platformConnections.shopify?.connected ? (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button 
                          variant="outline" 
                          className="rounded-full px-6 py-3 text-base font-semibold border-2 border-border/50 bg-background/90 backdrop-blur-sm text-foreground hover:bg-destructive hover:text-destructive-foreground hover:border-destructive transition-all duration-500 ease-in-out shadow-sm"
                        >
                          <X className="h-4 w-4 mr-2" />
                          Disconnect
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="rounded-3xl">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-xl font-bold">Disconnect Shopify?</AlertDialogTitle>
                          <AlertDialogDescription className="text-base leading-relaxed">
                            This will remove your Shopify connection. You can reconnect at any time.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter className="gap-2">
                          <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => disconnectPlatform('shopify')}
                            className="rounded-full bg-destructive hover:bg-destructive/90"
                          >
                            Disconnect
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  ) : (
                    <Dialog open={platformDialogOpen === 'shopify'} onOpenChange={(open) => setPlatformDialogOpen(open ? 'shopify' : null)}>
                      <DialogTrigger asChild>
                        <Button 
                          onClick={() => handlePlatformConnect('shopify')}
                          className="rounded-full px-6 py-3 text-base font-semibold bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground hover:from-primary/95 hover:via-primary/90 hover:to-primary/85 transition-all duration-500 ease-in-out shadow-lg hover:shadow-xl"
                        >
                          <Link2 className="h-4 w-4 mr-2" />
                          Connect
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="rounded-3xl max-w-md">
                        <DialogHeader>
                          <div className="flex items-center justify-between">
                            <div>
                              <DialogTitle className="text-xl font-bold">Connect Shopify</DialogTitle>
                              <DialogDescription className="text-sm text-muted-foreground mt-1">
                                Connect your Shopify store to publish articles to your blog
                              </DialogDescription>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.location.href = '/docs/shopify-integration'}
                              className="text-muted-foreground hover:text-foreground"
                            >
                              <BookOpen className="h-4 w-4 mr-1" />
                              Docs
                            </Button>
                          </div>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          {!showBlogSelection ? (
                            <>
                              <div className="space-y-2">
                                <Label htmlFor="shopify-admin-key">Admin API Key</Label>
                                <Input
                                  id="shopify-admin-key"
                                  type="password"
                                  placeholder="shpat_xxxxxxxxxxxxx"
                                  value={platformFormData.shopify?.adminApiKey || ''}
                                  onChange={(e) => setPlatformFormData({
                                    ...platformFormData,
                                    shopify: { ...platformFormData.shopify, adminApiKey: e.target.value } as any
                                  })}
                                />
                                <p className="text-xs text-muted-foreground flex items-start gap-1">
                                  <HelpCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                                  Found in: Apps → Develop apps → API credentials → Admin API access token
                                </p>
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="shopify-domain">Shopify Domain</Label>
                                <Input
                                  id="shopify-domain"
                                  placeholder="yourstore.myshopify.com"
                                  value={platformFormData.shopify?.domain || ''}
                                  onChange={(e) => setPlatformFormData({
                                    ...platformFormData,
                                    shopify: { ...platformFormData.shopify, domain: e.target.value } as any
                                  })}
                                />
                                <p className="text-xs text-muted-foreground flex items-start gap-1">
                                  <HelpCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                                  Your Shopify domain is displayed in the top-left corner of your admin panel
                                </p>
                              </div>
                            </>
                          ) : (
                            <div className="space-y-2">
                              <Label htmlFor="shopify-blog">Select Blog</Label>
                              <Select
                                value={platformFormData.shopify?.selectedBlogId || ''}
                                onValueChange={(value) => setPlatformFormData({
                                  ...platformFormData,
                                  shopify: { ...platformFormData.shopify, selectedBlogId: value } as any
                                })}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Choose a blog to publish to" />
                                </SelectTrigger>
                                <SelectContent>
                                  {shopifyBlogs.map((blog) => (
                                    <SelectItem key={blog.id} value={blog.id.toString()}>
                                      {blog.title}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => {
                            setPlatformDialogOpen(null);
                            setPlatformFormData({});
                            setShowBlogSelection(false);
                            setShopifyBlogs([]);
                          }} className="rounded-full">
                            Cancel
                          </Button>
                          {!showBlogSelection ? (
                            <Button 
                              onClick={verifyShopify}
                              disabled={isVerifying}
                              className="rounded-full bg-gradient-to-r from-primary via-primary to-primary/90"
                            >
                              {isVerifying ? 'Verifying...' : 'Verify'}
                            </Button>
                          ) : (
                            <Button 
                              onClick={() => savePlatformConnection('shopify')}
                              disabled={isSavingPlatform || !platformFormData.shopify?.selectedBlogId}
                              className="rounded-full bg-gradient-to-r from-primary via-primary to-primary/90"
                            >
                              {isSavingPlatform ? 'Connecting...' : 'Connect'}
                            </Button>
                          )}
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </div>
            </div>

            {/* Webflow */}
            <div className="p-6 rounded-3xl bg-gradient-to-br from-muted/30 to-muted/10 backdrop-blur-md border border-border/20 shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.04)] transition-all duration-300">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 rounded-2xl bg-background/90 border border-border/30 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                      <img src={PLATFORM_IMAGES.webflow} alt="Webflow" className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-foreground leading-tight">Webflow</h3>
                      <p className="text-sm text-muted-foreground font-medium mt-1">Publish articles to your Webflow CMS</p>
                    </div>
                  </div>
                  
                  {platformConnections.webflow?.connected ? (
                    <div className="flex items-center gap-3 flex-wrap">
                      <Badge variant="default" className="rounded-full px-3 py-1.5 font-semibold text-sm bg-green-500/10 text-green-600 border-green-500/20">
                        <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                        Connected
                      </Badge>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 flex-wrap">
                      <Badge variant="outline" className="rounded-full px-3 py-1.5 font-semibold text-sm text-muted-foreground border-border/50">
                        Not Connected
                      </Badge>
                    </div>
                  )}
                </div>
                
                <div className="flex-shrink-0">
                  {platformConnections.webflow?.connected ? (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button 
                          variant="outline" 
                          className="rounded-full px-6 py-3 text-base font-semibold border-2 border-border/50 bg-background/90 backdrop-blur-sm text-foreground hover:bg-destructive hover:text-destructive-foreground hover:border-destructive transition-all duration-500 ease-in-out shadow-sm"
                        >
                          <X className="h-4 w-4 mr-2" />
                          Disconnect
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="rounded-3xl">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-xl font-bold">Disconnect Webflow?</AlertDialogTitle>
                          <AlertDialogDescription className="text-base leading-relaxed">
                            This will remove your Webflow connection. You can reconnect at any time.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter className="gap-2">
                          <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => disconnectPlatform('webflow')}
                            className="rounded-full bg-destructive hover:bg-destructive/90"
                          >
                            Disconnect
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  ) : (
                    <Dialog open={platformDialogOpen === 'webflow'} onOpenChange={(open) => setPlatformDialogOpen(open ? 'webflow' : null)}>
                      <DialogTrigger asChild>
                        <Button 
                          onClick={() => handlePlatformConnect('webflow')}
                          className="rounded-full px-6 py-3 text-base font-semibold bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground hover:from-primary/95 hover:via-primary/90 hover:to-primary/85 transition-all duration-500 ease-in-out shadow-lg hover:shadow-xl"
                        >
                          <Link2 className="h-4 w-4 mr-2" />
                          Connect
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="rounded-3xl max-w-md">
                        <DialogHeader>
                          <div className="flex items-center justify-between">
                            <div>
                              <DialogTitle className="text-xl font-bold">Connect Webflow</DialogTitle>
                              <DialogDescription className="text-sm text-muted-foreground mt-1">
                                Enter your Webflow API key to publish articles
                              </DialogDescription>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.location.href = '/docs/webflow-integration'}
                              className="text-muted-foreground hover:text-foreground"
                            >
                              <BookOpen className="h-4 w-4 mr-1" />
                              Docs
                            </Button>
                          </div>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          {!showSiteSelection && !showCollectionSelection ? (
                            <div className="space-y-2">
                              <Label htmlFor="webflow-api-key">Webflow API Key</Label>
                              <Input
                                id="webflow-api-key"
                                type="password"
                                placeholder="your-api-key"
                                value={platformFormData.webflow?.apiKey || ''}
                                onChange={(e) => setPlatformFormData({
                                  ...platformFormData,
                                  webflow: { ...platformFormData.webflow, apiKey: e.target.value } as any
                                })}
                              />
                              <p className="text-xs text-muted-foreground flex items-start gap-1">
                                <HelpCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                                API key must have the following permissions: CMS: Read and write, Site: Read and write
                              </p>
                            </div>
                          ) : showSiteSelection ? (
                            <div className="space-y-2">
                              <Label htmlFor="webflow-site">Select Site</Label>
                              <Select
                                value={platformFormData.webflow?.siteId || ''}
                                onValueChange={handleWebflowSiteSelect}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Choose a site" />
                                </SelectTrigger>
                                <SelectContent>
                                  {webflowSites.map((site) => (
                                    <SelectItem key={site.id} value={site.id}>
                                      {site.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <p className="text-xs text-muted-foreground flex items-start gap-1">
                                <HelpCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                                Select the Webflow site where blog posts will be published
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <Label htmlFor="webflow-collection">Select Collection</Label>
                              <Select
                                value={platformFormData.webflow?.collectionId || ''}
                                onValueChange={(value) => setPlatformFormData({
                                  ...platformFormData,
                                  webflow: { ...platformFormData.webflow, collectionId: value } as any
                                })}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Choose a collection to publish to" />
                                </SelectTrigger>
                                <SelectContent>
                                  {webflowCollections.map((collection) => (
                                    <SelectItem key={collection.id} value={collection.id}>
                                      {collection.name}{collection.recommended ? ' (Recommended)' : ''}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <p className="text-xs text-muted-foreground flex items-start gap-1">
                                <HelpCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                                {webflowCollections.find((c: any) => c.recommended) 
                                  ? 'Blog Posts collection is recommended for publishing articles'
                                  : 'Select the CMS collection where blog posts will be published'}
                              </p>
                            </div>
                          )}
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => {
                            setPlatformDialogOpen(null);
                            setPlatformFormData({});
                            setShowCollectionSelection(false);
                            setShowSiteSelection(false);
                            setWebflowCollections([]);
                            setWebflowSites([]);
                          }} className="rounded-full">
                            Cancel
                          </Button>
                          {!showSiteSelection && !showCollectionSelection ? (
                            <Button 
                              onClick={verifyWebflow}
                              disabled={isVerifying}
                              className="rounded-full bg-gradient-to-r from-primary via-primary to-primary/90"
                            >
                              {isVerifying ? 'Verifying...' : 'Verify'}
                            </Button>
                          ) : showCollectionSelection ? (
                            <Button 
                              onClick={() => savePlatformConnection('webflow')}
                              disabled={isSavingPlatform || !platformFormData.webflow?.collectionId}
                              className="rounded-full bg-gradient-to-r from-primary via-primary to-primary/90"
                            >
                              {isSavingPlatform ? 'Connecting...' : 'Connect'}
                            </Button>
                          ) : null}
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </div>
            </div>

            {/* Framer */}
            <div className="p-6 rounded-3xl bg-gradient-to-br from-muted/30 to-muted/10 backdrop-blur-md border border-border/20 shadow-[0_2px_8px_rgba(0,0,0,0.02)] transition-all duration-300 opacity-60">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 rounded-2xl bg-background/90 border border-border/30 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                      <img src={PLATFORM_IMAGES.framer} alt="Framer" className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-foreground leading-tight">Framer</h3>
                      <p className="text-sm text-muted-foreground font-medium mt-1">Publish articles to your Framer CMS</p>
                    </div>
                  </div>
                  
                  {platformConnections.framer?.connected ? (
                    <div className="flex items-center gap-3 flex-wrap">
                      <Badge variant="default" className="rounded-full px-3 py-1.5 font-semibold text-sm bg-green-500/10 text-green-600 border-green-500/20">
                        <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                        Connected
                      </Badge>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 flex-wrap">
                      <Badge variant="outline" className="rounded-full px-3 py-1.5 font-semibold text-sm text-muted-foreground border-border/50">
                        Not Connected
                      </Badge>
                    </div>
                  )}
                </div>
                
                <div className="flex-shrink-0">
                  <Button 
                    disabled
                    className="rounded-full px-6 py-3 text-base font-semibold bg-muted text-muted-foreground cursor-not-allowed opacity-50"
                  >
                    <Link2 className="h-4 w-4 mr-2" />
                    Coming Soon
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    OAuth integration in development
                  </p>
                </div>
              </div>
            </div>

            {/* Wix */}
            <div className="p-6 rounded-3xl bg-gradient-to-br from-muted/30 to-muted/10 backdrop-blur-md border border-border/20 shadow-[0_2px_8px_rgba(0,0,0,0.02)] transition-all duration-300 opacity-50 cursor-not-allowed">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 rounded-2xl bg-background/90 border border-border/30 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                      <img src={PLATFORM_IMAGES.wix} alt="Wix" className="h-5 w-5 grayscale" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-foreground leading-tight">Wix</h3>
                      <p className="text-sm text-muted-foreground font-medium mt-1">Publish articles to your Wix blog</p>
                    </div>
                  </div>
                  
                </div>
                
                <div className="flex-shrink-0">
                  <Button 
                    disabled
                    className="rounded-full px-6 py-3 text-base font-semibold bg-muted text-muted-foreground cursor-not-allowed opacity-50"
                  >
                    <Link2 className="h-4 w-4 mr-2" />
                    Coming Soon
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Subscription Management */}
      <div>
        <SubscriptionManagement user={user} />
      </div>
    </div>
  );
};